import React, { useState } from 'react';
import { ChevronRight, Hand } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';

const workflowFeatures = [
  { name: 'CareConnect/EVV', activated: false },
  { name: 'A-Fax', activated: false },
  { name: 'Telephony', activated: false },
  { name: 'Text Notification', activated: false },
  { name: 'Ivory Offline Features', activated: false },
  { name: 'Delayed Visit Notifications', activated: false },
  { name: 'Open Shift Notifications', activated: false },
  { name: 'CRM', activated: false },
];

const billingSolutions = [
  { name: 'Availity', activated: false },
  { name: 'Inovalon', activated: false },
  { name: 'Inovalon Eligibility Verification Standard', activated: false },
];

const partnerSolutions = [
  { name: 'Growth Solutions & Industry Certifications', icon: '📈', hasSubmenu: true },
  { name: 'Payroll Solutions', icon: '💵', hasSubmenu: true },
  { name: 'HR Solutions', icon: '👥', hasSubmenu: true },
  { name: 'Billing & Coding Solutions', icon: '💰', hasSubmenu: true },
];

const documentationFeatures = [
  { name: 'Custom Documents', activated: false },
];

const medicareFeatures = [
  { name: 'Medicare DDE and Eligibility', activated: false },
];

const specialConfigs = [
  { name: 'Hospice', activated: false },
];

const FeaturesActivationPage = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const FeatureItem = ({ name, activated, hasSubmenu = false, icon = null }) => (
    <div className="flex items-center justify-between py-3 px-4 border-b border-gray-100 hover:bg-gray-50 transition-colors">
      <div className="flex items-center gap-2">
        <ChevronRight className="w-4 h-4 text-gray-400" />
        {icon && <span>{icon}</span>}
        <span className="text-gray-700">{name}</span>
      </div>
      {!hasSubmenu && (
        <span className={`text-xs px-3 py-1 rounded ${
          activated 
            ? 'bg-green-100 text-green-700' 
            : 'bg-gray-200 text-gray-600'
        }`}>
          {activated ? 'Activated' : 'Not Activated'}
        </span>
      )}
    </div>
  );

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-light text-gray-700">
            Features <span className="text-gray-400 text-lg">» Activation</span>
          </h1>
        </div>

        {/* Notice */}
        <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
          <p className="text-orange-700 text-sm">
            NOTE: All requests will be responded to within 1 – 2 business days.
          </p>
        </div>

        {/* Request Button */}
        <div className="flex justify-center mb-8">
          <button className="flex items-center gap-3 bg-[#5cb85c] hover:bg-[#4cae4c] text-white px-8 py-3 rounded-lg font-medium transition-colors shadow-md">
            <Hand className="w-5 h-5" />
            Request Additional Licenses
          </button>
        </div>

        <div className="grid grid-cols-2 gap-8">
          {/* Left Column */}
          <div>
            {/* Workflow Efficiency */}
            <div className="mb-6">
              <h2 className="flex items-center gap-2 text-[#e67e22] font-semibold mb-3">
                <span>🔧</span> Workflow Efficiency
              </h2>
              <div className="bg-white rounded-lg shadow-sm border">
                {workflowFeatures.map((feature, idx) => (
                  <FeatureItem key={idx} {...feature} />
                ))}
              </div>
            </div>

            {/* Billing Solutions */}
            <div className="mb-6">
              <h2 className="flex items-center gap-2 text-[#e67e22] font-semibold mb-3">
                <span>≡</span> Billing Solutions
              </h2>
              <div className="bg-white rounded-lg shadow-sm border">
                {billingSolutions.map((feature, idx) => (
                  <FeatureItem key={idx} {...feature} />
                ))}
              </div>
            </div>

            {/* Partner Solutions */}
            <div className="mb-6">
              <h2 className="flex items-center gap-2 text-[#e67e22] font-semibold mb-3">
                <span>👥</span> Partner Solutions
              </h2>
              <div className="bg-white rounded-lg shadow-sm border">
                {partnerSolutions.map((feature, idx) => (
                  <FeatureItem key={idx} {...feature} hasSubmenu />
                ))}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div>
            {/* Documentation */}
            <div className="mb-6">
              <h2 className="flex items-center gap-2 text-[#e67e22] font-semibold mb-3">
                <span>📄</span> Documentation
              </h2>
              <div className="bg-white rounded-lg shadow-sm border">
                {documentationFeatures.map((feature, idx) => (
                  <FeatureItem key={idx} {...feature} />
                ))}
              </div>
            </div>

            {/* Medicare Integration */}
            <div className="mb-6">
              <h2 className="flex items-center gap-2 text-[#e67e22] font-semibold mb-3">
                Medicare Integration
              </h2>
              <div className="bg-white rounded-lg shadow-sm border">
                {medicareFeatures.map((feature, idx) => (
                  <FeatureItem key={idx} {...feature} />
                ))}
              </div>
            </div>

            {/* Special Configurations */}
            <div className="mb-6">
              <h2 className="flex items-center gap-2 text-[#e67e22] font-semibold mb-3">
                <span>🔧</span> Special Configurations
              </h2>
              <div className="bg-white rounded-lg shadow-sm border">
                {specialConfigs.map((feature, idx) => (
                  <FeatureItem key={idx} {...feature} />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-gray-500 text-sm mt-8 pt-4 border-t">
          © 2026 Ivory Healthcare Systems, LLC. <span className="float-right">Version: 8.61.4.19</span>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FeaturesActivationPage;
